import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { TrendingUp, DollarSign, Home, Users, Calendar, Wallet } from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import axios from 'axios';
import { BACKEND_URL } from '../../utils/config';

const API = `${BACKEND_URL}/api`;

const KPIFinanziariAvanzati = ({ filters }) => {
  const [financialData, setFinancialData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFinancialData();
  }, [filters]);

  const fetchFinancialData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      
      const params = new URLSearchParams({
        year: filters.year
      });
      
      if (filters.period === 'month' && filters.month) {
        params.append('month', filters.month);
      }
      if (filters.propertyId) {
        params.append('property_id', filters.propertyId);
      }
      
      const response = await axios.get(`${API}/kpi/finanziari-avanzati?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFinancialData(response.data);
      
    } catch (error) {
      console.error('Error fetching financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !financialData) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  // Prepare chart data for monthly comparison
  const monthlyComparisonData = Object.keys(financialData.cash_based.monthly).map(month => ({
    month: `Mese ${month}`,
    perCassa: financialData.cash_based.monthly[month] || 0,
    perCompetenza: financialData.accrual_based.monthly[month] || 0
  }));

  // Prepare data for property comparison
  const propertyData = Object.keys(financialData.revenue_by_property).map(propId => ({
    property: propId.substring(0, 8) + '...',
    ricavi: financialData.revenue_by_property[propId],
    perNotte: financialData.avg_per_night_by_property[propId]
  }));

  // Prepare data for platform comparison
  const platformData = Object.keys(financialData.revenue_by_platform).map(platform => ({
    name: platform,
    ricavi: financialData.revenue_by_platform[platform],
    perNotte: financialData.avg_per_night_by_platform[platform]
  }));

  const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
    <Card className="p-6 bg-gradient-to-br from-white to-gray-50 shadow-lg hover:shadow-xl transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full bg-gradient-to-br ${color.replace('text-', 'from-')}-100 ${color.replace('text-', 'to-')}-200`}>
          <Icon className={`h-8 w-8 ${color}`} />
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">💰 KPI Finanziari Avanzati</h2>
        <p className="text-gray-600">Analisi dettagliata incassi per cassa e competenza</p>
      </div>

      {/* Main KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Ricavi Totali (Cassa)"
          value={`€${financialData.total_revenue_cash.toLocaleString('it-IT', {minimumFractionDigits: 2})}`}
          subtitle="Incassato nel periodo"
          icon={Wallet}
          color="text-green-600"
        />
        
        <StatCard
          title="Ricavi Totali (Competenza)"
          value={`€${financialData.total_revenue_accrual.toLocaleString('it-IT', {minimumFractionDigits: 2})}`}
          subtitle="Fatturato nel periodo"
          icon={DollarSign}
          color="text-blue-600"
        />
        
        <StatCard
          title="Guadagno Medio per Notte"
          value={`€${financialData.avg_revenue_per_night.toLocaleString('it-IT', {minimumFractionDigits: 2})}`}
          subtitle={`Su ${financialData.total_nights} notti`}
          icon={Calendar}
          color="text-purple-600"
        />
        
        <StatCard
          title="Notti Totali"
          value={financialData.total_nights.toLocaleString()}
          subtitle="Periodo analizzato"
          icon={Calendar}
          color="text-slate-600"
        />
      </div>

      {/* Incassi Medi */}
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-4">📊 Incassi Medi per Periodo</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Per Cassa */}
          <Card className="p-6">
            <h4 className="text-lg font-semibold text-green-700 mb-4">💵 Per Cassa</h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="font-medium text-gray-700">Giornaliero</span>
                <span className="font-bold text-green-700">€{financialData.cash_based.avg_daily.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="font-medium text-gray-700">Settimanale</span>
                <span className="font-bold text-green-700">€{financialData.cash_based.avg_weekly.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="font-medium text-gray-700">Mensile</span>
                <span className="font-bold text-green-700">€{financialData.cash_based.avg_monthly.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
              </div>
            </div>
          </Card>

          {/* Per Competenza */}
          <Card className="p-6">
            <h4 className="text-lg font-semibold text-blue-700 mb-4">📈 Per Competenza</h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="font-medium text-gray-700">Giornaliero</span>
                <span className="font-bold text-blue-700">€{financialData.accrual_based.avg_daily.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="font-medium text-gray-700">Settimanale</span>
                <span className="font-bold text-blue-700">€{financialData.accrual_based.avg_weekly.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="font-medium text-gray-700">Mensile</span>
                <span className="font-bold text-blue-700">€{financialData.accrual_based.avg_monthly.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Monthly Comparison Chart */}
      {monthlyComparisonData.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">📊 Confronto Mensile: Cassa vs Competenza</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={monthlyComparisonData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: '12px' }} />
              <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
              <Tooltip 
                formatter={(value) => `€${value.toLocaleString('it-IT', {minimumFractionDigits: 2})}`}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
              <Legend />
              <Bar dataKey="perCassa" fill="#10b981" name="Per Cassa" radius={[8, 8, 0, 0]} />
              <Bar dataKey="perCompetenza" fill="#3b82f6" name="Per Competenza" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Revenue by Property */}
      {propertyData.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">🏠 Ricavi per Immobile</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={propertyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="property" stroke="#6b7280" style={{ fontSize: '11px' }} />
              <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
              <Tooltip 
                formatter={(value) => `€${value.toLocaleString('it-IT', {minimumFractionDigits: 2})}`}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
              <Legend />
              <Bar dataKey="ricavi" fill="#8b5cf6" name="Ricavi Totali" radius={[8, 8, 0, 0]} />
              <Bar dataKey="perNotte" fill="#f59e0b" name="Ricavo per Notte" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Revenue by Platform */}
      {platformData.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">🌐 Ricavi per Piattaforma</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={platformData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" stroke="#6b7280" style={{ fontSize: '12px' }} />
              <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
              <Tooltip 
                formatter={(value) => `€${value.toLocaleString('it-IT', {minimumFractionDigits: 2})}`}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
              <Legend />
              <Bar dataKey="ricavi" fill="#0ea5e9" name="Ricavi Totali" radius={[8, 8, 0, 0]} />
              <Bar dataKey="perNotte" fill="#f97316" name="Ricavo per Notte" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Detailed Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Properties Table */}
        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">📋 Dettaglio per Immobile</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-2 px-3 font-semibold text-gray-700">Immobile</th>
                  <th className="text-right py-2 px-3 font-semibold text-gray-700">Ricavi</th>
                  <th className="text-right py-2 px-3 font-semibold text-gray-700">Per Notte</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(financialData.revenue_by_property).map(propId => (
                  <tr key={propId} className="border-b border-gray-100">
                    <td className="py-2 px-3 text-sm">{propId.substring(0, 12)}...</td>
                    <td className="text-right py-2 px-3 font-semibold text-gray-900">
                      €{financialData.revenue_by_property[propId].toLocaleString('it-IT', {minimumFractionDigits: 2})}
                    </td>
                    <td className="text-right py-2 px-3 font-semibold text-blue-600">
                      €{financialData.avg_per_night_by_property[propId].toLocaleString('it-IT', {minimumFractionDigits: 2})}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Platforms Table */}
        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">📋 Dettaglio per Piattaforma</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-2 px-3 font-semibold text-gray-700">Piattaforma</th>
                  <th className="text-right py-2 px-3 font-semibold text-gray-700">Ricavi</th>
                  <th className="text-right py-2 px-3 font-semibold text-gray-700">Per Notte</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(financialData.revenue_by_platform).map(platform => (
                  <tr key={platform} className="border-b border-gray-100">
                    <td className="py-2 px-3 text-sm font-medium">{platform}</td>
                    <td className="text-right py-2 px-3 font-semibold text-gray-900">
                      €{financialData.revenue_by_platform[platform].toLocaleString('it-IT', {minimumFractionDigits: 2})}
                    </td>
                    <td className="text-right py-2 px-3 font-semibold text-green-600">
                      €{financialData.avg_per_night_by_platform[platform].toLocaleString('it-IT', {minimumFractionDigits: 2})}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default KPIFinanziariAvanzati;
